/*
 * Copyright (c) CIRROM 2026.
 */

import { useEffect, useMemo, useState } from "react";
import {
  getCRMURLForSuggestedChange,
  getExplainForSuggestedChange,
} from "../../../api/suggestedchanges";
import {
  CirromMonitorCRMComparison,
  getMonitorCRMComparison,
} from "../../../api/cirrommonitor";
import { ExplainResponse, normalizeRecordReview } from "./normalize";
import { Loadable, useLoadCycle } from "../../utils/useLoadCycle";

/*
 * Loading a record for review, from either of the two ways in.
 *
 * The page is reached by URL and knows the record and system up front. The
 * modal is opened from the suggested-changes grid, whose rows carry a change
 * id and a record id but no system id - so there the system is only knowable
 * once the explain payload comes back, and the Monitor comparison has to wait
 * for it. Both callers want the same model out, so the difference is absorbed
 * here rather than in two copies of the fetch.
 */

export interface RecordReviewIds {
  integratedSystemId?: number | null;
  recordId?: number | null;
  suggestedChangeId?: number | null;
  batchId?: number | null;
}

export const useRecordReview = (ids: RecordReviewIds, enabled = true) => {
  const { integratedSystemId, recordId, suggestedChangeId, batchId } = ids;

  const [explain, setExplain] = useState<Loadable<ExplainResponse>>(undefined);
  const [monitor, setMonitor] =
    useState<Loadable<CirromMonitorCRMComparison>>(undefined);
  const [crmUrls, setCrmUrls] = useState<{
    contact?: string;
    account?: string;
  } | null>(null);
  const idsKey = `${integratedSystemId}:${recordId}:${suggestedChangeId}:${batchId}`;
  /*
   * Silent reloads (after a write) keep the current card on screen while
   * fresh data arrives; loud ones (navigation, retry) show the logo.
   */
  const { reloadToken, startLoad, reload, reloadSilently } =
    useLoadCycle(idsKey);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const { showLoading } = startLoad();
    if (showLoading) {
      setExplain(undefined);
      setMonitor(undefined);
      setCrmUrls(null);
    }

    // Nothing identifies a record: not a load, a bad address.
    if (!recordId && !suggestedChangeId) {
      setExplain(null);
      setMonitor(null);
      return;
    }

    /*
     * Monitor is keyed on record + system. When the caller supplied both we
     * start it immediately, in parallel with explain; when it did not, this
     * runs again with the ids explain reported.
     */
    const loadMonitor = (record: number, system: number) => {
      if (!record || !system) {
        setMonitor(null);
        return;
      }
      getMonitorCRMComparison(record, system)
        .then((response) => {
          if (!cancelled) setMonitor(response);
        })
        .catch(() => {
          if (!cancelled) setMonitor(null);
        });
    };

    getExplainForSuggestedChange(
      suggestedChangeId ?? null,
      batchId ?? null,
      recordId ?? null,
      integratedSystemId ?? null
    )
      .then((response) => {
        if (cancelled) return;
        setExplain(response);
        if (!integratedSystemId || !recordId) {
          loadMonitor(
            recordId || response?.record_id,
            integratedSystemId || response?.integrated_system_id
          );
        }
        /*
         * CRM deep links come with the explain payload when the backend can
         * build them from the record itself. Only without them is the older
         * change-keyed lookup worth a request: it hangs off a change id, so
         * prefer the id the caller handed over, otherwise borrow the first
         * pending change, exactly as the suggested-changes modal does.
         */

        const explainUrls = response?.crm_urls;
        if (explainUrls?.contact || explainUrls?.account) {
          return;
        }
        const firstChangeId =
          suggestedChangeId ??
          response?.contact_data
            ?.flatMap((entry) => entry.cirrom_changes ?? [])
            .map((change) => change.suggested_change_id)
            .find(Boolean);
        if (firstChangeId) {
          getCRMURLForSuggestedChange(firstChangeId)
            .then((urls) => {
              if (!cancelled) {
                setCrmUrls({ contact: urls?.contact, account: urls?.account });
              }
            })
            .catch(() => {
              // Links are a convenience; the record stands without them.
            });
        }
      })
      .catch(() => {
        if (cancelled) return;
        setExplain(null);
        // Without explain there is no way to learn the system id, so a
        // caller that did not supply one has no Monitor either.
        if (!integratedSystemId || !recordId) setMonitor(null);
      });

    if (integratedSystemId && recordId) {
      loadMonitor(recordId, integratedSystemId);
    }

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey, reloadToken, enabled]);

  const isLoading = explain === undefined || monitor === undefined;
  const bothFailed = explain === null && monitor === null;

  const model = useMemo(
    () =>
      !isLoading && !bothFailed
        ? normalizeRecordReview(explain ?? null, monitor ?? null, crmUrls)
        : null,
    [isLoading, bothFailed, explain, monitor, crmUrls]
  );

  /*
   * Named as sources, not as descriptions of what they carry: the ledger puts
   * these into a sentence.
   */
  const unavailableSources = [
    ...(explain === null && !bothFailed ? ["Cirrom data quality"] : []),
    ...(monitor === null && !bothFailed ? ["LinkedIn Monitor"] : []),
  ];

  return {
    model,
    bothFailed,
    unavailableSources,
    /** The ids actually in play, once explain has reported them. */
    effectiveRecordId: recordId || explain?.record_id,
    effectiveSystemId: integratedSystemId || explain?.integrated_system_id,
    reloadSilently,
    reload,
  };
};
